import React, { useState, useEffect, useCallback } from 'react';
import { AppBar, Toolbar, Box, useTheme, Button, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import GenerateReportPage from './pages/GenerateReportPage';
import ChooseReportTargetPage from './pages/ChooseReportTargetPage';
import LoginPage from './pages/LoginPage';
import ManageSpirisConnectionsPage from './pages/ManageSpirisConnectionsPage';
import NoAccessPage from './pages/NoAccessPage';
import ThemeToggle from './features/theme/components/ThemeToggle';
import { ReportScopeDataProvider } from './features/report/context/ReportScopeDataContext';
import { useRouteAuthenticatedUser } from './features/auth/hooks/useRouteAuthenticatedUser';

type ShellProps = {
  children: React.ReactNode;
};

type AppShellProps = ShellProps & {
  isAuthenticated?: boolean;
  makrillRotation?: number;
  onToggleMakrill?: () => void;
  onLogout?: () => void | Promise<void>;
};

const readMakrillAnimationPreference = () => {
  const stored = localStorage.getItem('i_love_gais');
  return stored ? JSON.parse(stored) : true;
};

const AppShell: React.FC<AppShellProps> = React.memo(({
  children,
  isAuthenticated = false,
  makrillRotation = 0,
  onToggleMakrill,
  onLogout,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        }}
      >
        <Toolbar
          sx={{
            py: { xs: 1, sm: 2 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src="/Ictech_logotyp_svart.png"
            alt="ictech"
            sx={{
              height: { xs: 36, sm: 50 },
              width: 'auto',
              filter: 'brightness(0) invert(1)',
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isAuthenticated && onToggleMakrill && (
              <Button
                onClick={onToggleMakrill}
                sx={{
                  minWidth: 0,
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Box
                  component="img"
                  src="/gais-makrill.png"
                  alt="makrill"
                  sx={{
                    width: 24,
                    height: 24,
                    transform: `rotate(${makrillRotation}deg)`,
                    transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                />
              </Button>
            )}
            <ThemeToggle />
            {isAuthenticated && onLogout && (
              <Button
                onClick={() => {
                  void onLogout();
                }}
                sx={{
                  color: theme.palette.common.white,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Logga ut
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  );
});

const App: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const [makrillAnimationEnabled, setMakrillAnimationEnabled] = useState(() => readMakrillAnimationPreference());
  const [makrillRotation, setMakrillRotation] = useState(() => readMakrillAnimationPreference() ? 0 : 180);
  const {
    isAuthenticated,
    isLoading,
    spirisConnectionNotice,
    isAllAccess,
    isAdmin,
    currentTarget,
    currentProfileKey,
    profileReloadKey,
    clearClientAuthState,
    handleOpenReportForProfile,
    handleReportTargetSelected,
    handleSwitchCompany,
    handleManageSpirisConnections,
    handleLogout,
  } = useRouteAuthenticatedUser();

  // Persist makrill animation preference
  useEffect(() => {
    localStorage.setItem('i_love_gais', JSON.stringify(makrillAnimationEnabled));
  }, [makrillAnimationEnabled]);

  const handleToggleMakrill = useCallback(() => {
    setMakrillAnimationEnabled((prev: boolean) => !prev);
    setMakrillRotation((prev) => prev + 180);
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={(
            <AppShell>
              <LoginPage />
            </AppShell>
          )}
        />
        <Route
          path="/admin"
          element={(
            <AppShell>
              <LoginPage />
            </AppShell>
          )}
        />
        <Route
          path="/no-access"
          element={(
            <AppShell>
              <NoAccessPage
                onBackToLogin={() => {
                  clearClientAuthState();
                  window.location.assign('/login');
                }}
              />
            </AppShell>
          )}
        />
        <Route
          path="*"
          element={(
            <Navigate to="/login" replace />
          )}
        />
      </Routes>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/admin"
          element={(
            <AppShell
              isAuthenticated={true}
              makrillRotation={makrillRotation}
              onToggleMakrill={handleToggleMakrill}
              onLogout={handleLogout}
            >
              <ManageSpirisConnectionsPage
                notice={spirisConnectionNotice}
                onOpenReport={handleOpenReportForProfile}
              />
            </AppShell>
          )}
        />
        <Route
          path="/select"
          element={(
            <AppShell
              isAuthenticated={true}
              makrillRotation={makrillRotation}
              onToggleMakrill={handleToggleMakrill}
              onLogout={handleLogout}
            >
              <ChooseReportTargetPage
                onSelected={handleReportTargetSelected}
                canManageSpirisConnections={isAdmin}
                onManageSpirisConnections={handleManageSpirisConnections}
              />
            </AppShell>
          )}
        />
        <Route
          path="/report/*"
          element={(
            <AppShell
              isAuthenticated={true}
              makrillRotation={makrillRotation}
              onToggleMakrill={handleToggleMakrill}
              onLogout={handleLogout}
            >
              <ReportScopeDataProvider
                reloadKey={profileReloadKey}
                enabled={location.pathname.startsWith('/report')}
                currentProfileKey={currentProfileKey}
                currentTarget={currentTarget}
              >
                <GenerateReportPage
                  canSwitchCompany={isAllAccess || isAdmin}
                  canManageSpirisConnections={isAdmin}
                  onSwitchCompany={handleSwitchCompany}
                  onManageSpirisConnections={handleManageSpirisConnections}
                  currentProfileKey={currentProfileKey}
                  currentTarget={currentTarget}
                  makrillAnimationEnabled={makrillAnimationEnabled}
                />
              </ReportScopeDataProvider>
            </AppShell>
          )}
        />
        <Route path="/" element={<Navigate to="/report" replace />} />
        <Route path="*" element={<Navigate to="/report" replace />} />
      </Routes>
    </>
  );
};

export default App;
